import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useContact } from '../../hooks/useContacts';
import { contactsService, ContactFormData } from '../../services/contactsService';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { Camera, User } from 'phosphor-react';

const ContactForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const { contact, loading: contactLoading } = useContact(id || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [contactId, setContactId] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<'customer' | 'vendor' | 'employee' | 'other'>('customer');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const generateNewContactId = useCallback(async () => {
    setGeneratingId(true);
    try {
      const generatedId = await contactsService.generateContactId(businessId || undefined);
      setContactId(generatedId);
    } catch (error) {
      console.error('Failed to generate contact ID:', error);
      // Fallback to timestamp-based ID
      setContactId(`C-${Date.now()}`);
    }
    setGeneratingId(false);
  }, [businessId]);

  useEffect(() => {
    if (contact) {
      setContactId(contact.contact_id);
      setName(contact.name);
      setType(contact.type as 'customer' | 'vendor' | 'employee' | 'other');
      setEmail(contact.email || '');
      setPhone(contact.phone || '');
      setAddress(contact.address || '');
      setProfilePictureUrl(contact.profile_picture_url || '');
    } else if (!id && isAuthenticated && !authLoading) {
      // Generate contact ID for new contacts only when authenticated
      generateNewContactId();
    }
  }, [contact, id, isAuthenticated, authLoading, generateNewContactId]);

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB');
        return;
      }

      setError('');

      // Auto-upload the image
      setUploadingImage(true);
      try {
        const url = await contactsService.uploadProfilePicture(file, contactId || 'temp', businessId || undefined);
        if (url) {
          setProfilePictureUrl(url);
        }
      } catch (uploadError) {
        console.error('Failed to upload profile picture:', uploadError);
        setError('Failed to upload profile picture');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      // Check businessId availability

      if (!businessId) {
        setError('Business information not available. Please try logging out and logging back in.');
        setLoading(false);
        return;
      }

      const contactData: ContactFormData = {
        contact_id: contactId,
        name: name.trim(),
        type,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        profile_picture_url: profilePictureUrl || undefined,
      };

      let result;

      if (id) {
        // Update existing contact
        result = await contactsService.updateContact(id, contactData);
      } else {
        // Create new contact
        result = await contactsService.createContact(contactData, businessId);
      }

      if (result.error) {
        setError(result.error.message);
      } else {
        navigate('/contacts');
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to save contact');
    }

    setLoading(false);
  };

  // Show loading while fetching contact data or auth/business is loading
  if ((contactLoading && id) || authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {(contactLoading && id) ? 'Loading contact...' :
           authLoading ? 'Loading authentication...' :
           'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can manage contacts.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access the contact management system.
          </p>
        </div>
      </div>
    );
  }

  const breadcrumbItems = id ? [
    { label: 'Contacts', href: '/contacts' },
    { label: contact?.name || 'Contact', href: `/contacts/${id}` },
    { label: 'Edit Contact', current: true }
  ] : [
    { label: 'Contacts', href: '/contacts' },
    { label: 'Create New Contact', current: true }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Contact' : 'Add New Contact'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {id ? 'Update contact information' : 'Create a new contact for your business'}
        </p>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <form onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Profile Picture Section */}
          <div className="px-6 py-8 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-gray-200 border-4 border-white shadow-lg overflow-hidden">
                  {profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <User className="w-12 h-12 text-gray-400" weight="bold" />
                    </div>
                  )}
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                  disabled={uploadingImage}
                >
                  <Camera className="w-4 h-4" weight="bold" />
                </button>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">Profile Picture</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Click the camera icon to upload a photo
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG, GIF up to 5MB
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Form Fields */}
          <div className="px-6 py-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-6">
                <Input
                  label="Contact ID"
                  type="text"
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value)}
                  required
                  disabled={generatingId}
                  placeholder={generatingId ? 'Generating...' : 'C-123456789-1'}
                />

                <Input
                  label="Full Name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Enter full name"
                />

                <Select
                  label="Contact Type"
                  value={type}
                  onChange={(e) => setType(e.target.value as any)}
                  options={[
                    { value: 'customer', label: 'Customer' },
                    { value: 'vendor', label: 'Vendor' },
                    { value: 'employee', label: 'Employee' },
                    { value: 'other', label: 'Other' },
                  ]}
                  required
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contact@example.com"
                />

                <Input
                  label="Phone Number"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+62 812-3456-7890"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    className="input-field resize-none"
                    rows={4}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter address (optional)"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (id) {
                  // If editing, go back to contact details
                  navigate(`/contacts/${id}`);
                } else {
                  // If creating new, go back to contact list
                  navigate('/contacts');
                }
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || generatingId || uploadingImage}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                id ? 'Update Contact' : 'Create Contact'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;